/**
 * Handoff from an insight card (Home's "View insight", Spotlight's "Dive deep
 * with DANI") to a new DANI chat that already shows the card's finding
 * (docs/pdlc/insights-dive-deep.md §3.2).
 *
 * Neither surface mounts `StudioAIProvider` (it lives under /dani), so the
 * exchange rides in sessionStorage to `/dani?launch=insight` and the DANI
 * landing page reads it once, inside the provider. Same shape as the Reports
 * handoff (`src/@reports/utils/kickoff.ts`).
 */

import type { SeededMessage } from '@/@discover/insightExchange';

export const INSIGHT_LAUNCH_QUERY_VALUE = 'insight';
export const INSIGHT_LAUNCH_URL = `/dani?launch=${INSIGHT_LAUNCH_QUERY_VALUE}`;

const LAUNCH_STORAGE_KEY = 'mfour.discover.insight-launch';
const LAUNCH_TTL_MS = 5 * 60 * 1000;

/** What a card hands to DANI: the exchange to show, and the question to pre-fill. */
export interface InsightLaunch {
    messages: SeededMessage[];
    /** The card's follow-up question (`daniSeedQuery`), put in the composer unsent. */
    followUp: string;
    /** The saved chat's name, the card's headline, in place of DANI's "[Copy] …". */
    title: string;
    insightId: string;
}

/** False when storage is unavailable; the caller then takes the fallback. */
export function storeInsightLaunch(launch: InsightLaunch): boolean {
    try {
        sessionStorage.setItem(LAUNCH_STORAGE_KEY, JSON.stringify({ ...launch, ts: Date.now() }));
        return true;
    } catch {
        return false;
    }
}

function isSeededMessage(value: unknown): value is SeededMessage {
    if (!value || typeof value !== 'object') return false;
    const m = value as Partial<SeededMessage>;
    return (
        typeof m.id === 'string' &&
        (m.role === 'user' || m.role === 'assistant') &&
        Array.isArray(m.content) &&
        m.content.length > 0
    );
}

/** Reads and deletes the launch; null when absent, invalid, or stale. */
export function consumeInsightLaunch(): InsightLaunch | null {
    try {
        const raw = sessionStorage.getItem(LAUNCH_STORAGE_KEY);
        if (!raw) return null;
        sessionStorage.removeItem(LAUNCH_STORAGE_KEY);
        const parsed = JSON.parse(raw) as Partial<InsightLaunch> & { ts?: unknown };
        if (typeof parsed.ts !== 'number' || Date.now() - parsed.ts > LAUNCH_TTL_MS) return null;
        const { messages } = parsed;
        if (!Array.isArray(messages) || messages.length === 0) return null;
        if (!messages.every(isSeededMessage)) return null;
        return {
            messages,
            followUp: typeof parsed.followUp === 'string' ? parsed.followUp : '',
            title: typeof parsed.title === 'string' ? parsed.title : '',
            insightId: typeof parsed.insightId === 'string' ? parsed.insightId : '',
        };
    } catch {
        return null;
    }
}
